import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import authRecoveryService from '../../../services/authRecoveryService';

const FailedUserCreationsPanel = ({ refreshTrigger, onRetryComplete }) => {
  const [failedCreations, setFailedCreations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(null);
  const [bulkSelection, setBulkSelection] = useState([]);

  useEffect(() => {
    loadFailedCreations();
  }, [refreshTrigger]);

  const loadFailedCreations = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await authRecoveryService?.getFailedUserCreations();
      setFailedCreations(data);
    } catch (err) {
      setError(err?.message || 'Failed to load failed user creations');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async (failedCreationId) => {
    try {
      setProcessing(failedCreationId);
      setError('');
      const result = await authRecoveryService?.retryFailedUserCreation(failedCreationId);
      alert(`${result?.message}\n\n${result?.retryInstructions}`);
      await loadFailedCreations();
      onRetryComplete?.();
    } catch (err) {
      setError(err?.message || 'Failed to retry user creation');
    } finally {
      setProcessing(null);
    }
  };

  const handleBulkRetry = async () => {
    if (bulkSelection?.length === 0) return;
    if (!confirm(`Retry ${bulkSelection?.length} failed user creation(s)?`)) return;

    try {
      setLoading(true);
      setError('');
      const results = await authRecoveryService?.bulkRetryUserCreations(bulkSelection);
      const successCount = results?.filter(r => r?.success)?.length;
      alert(`Successfully initiated retry for ${successCount} of ${bulkSelection?.length} user creations`);
      setBulkSelection([]);
      await loadFailedCreations();
      onRetryComplete?.();
    } catch (err) {
      setError(err?.message || 'Bulk retry failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleBulkSelection = (id) => {
    setBulkSelection(prev =>
      prev?.includes(id)
        ? prev?.filter(selectedId => selectedId !== id)
        : [...prev, id]
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400';
      case 'retrying':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400';
      case 'recovered':
        return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400';
      case 'failed_permanently':
        return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400';
      default:
        return 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400';
    }
  };

  if (loading && failedCreations?.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" className="w-6 h-6 text-emerald-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
            Failed User Creation Recovery
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Bulk retry operations with error analysis and automated account provisioning
          </p>
        </div>
        {bulkSelection?.length > 0 && (
          <Button size="sm" onClick={handleBulkRetry}>
            <Icon name="RefreshCw" size={14} className="mr-1" />
            Bulk Retry ({bulkSelection?.length})
          </Button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-slate-700 border-b border-gray-200 dark:border-slate-600">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={bulkSelection?.length === failedCreations?.filter(fc => fc?.recoveryStatus === 'pending')?.length && failedCreations?.filter(fc => fc?.recoveryStatus === 'pending')?.length > 0}
                    onChange={(e) => {
                      if (e?.target?.checked) {
                        setBulkSelection(failedCreations?.filter(fc => fc?.recoveryStatus === 'pending')?.map(fc => fc?.id));
                      } else {
                        setBulkSelection([]);
                      }
                    }}
                    className="rounded border-gray-300 dark:border-slate-600"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">User Details</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Failure Reason</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Retry Count</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
              {failedCreations?.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-sm text-gray-600 dark:text-gray-400">
                    <Icon name="CheckCircle" className="w-12 h-12 mx-auto mb-2 text-green-500" />
                    <p>No failed user creations</p>
                  </td>
                </tr>
              ) : (
                failedCreations?.map((creation) => (
                  <tr key={creation?.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                    <td className="px-4 py-3">
                      {creation?.recoveryStatus === 'pending' && (
                        <input
                          type="checkbox"
                          checked={bulkSelection?.includes(creation?.id)}
                          onChange={() => toggleBulkSelection(creation?.id)}
                          className="rounded border-gray-300 dark:border-slate-600"
                        />
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{creation?.fullName || 'Unknown'}</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">{creation?.email}</p>
                        {creation?.companyName && (
                          <p className="text-xs text-gray-500 dark:text-gray-500">{creation?.companyName}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-xs text-gray-900 dark:text-white">
                        {creation?.failureReason}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 text-xs font-bold rounded bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400">
                        {creation?.retryCount || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs font-bold rounded ${getStatusColor(creation?.recoveryStatus)}`}>
                        {creation?.recoveryStatus?.replace('_', ' ')?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {creation?.recoveryStatus === 'pending' && (
                        <Button
                          size="sm"
                          onClick={() => handleRetry(creation?.id)}
                          disabled={processing === creation?.id}
                          loading={processing === creation?.id}
                        >
                          <Icon name="RefreshCw" size={14} className="mr-1" />
                          Retry
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <Icon name="Info" className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-blue-900 dark:text-blue-200">Retry Instructions</p>
            <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
              Clicking "Retry" will mark the user creation for retry. The user should then complete the signup process using the same email address. The system will automatically link the account and complete provisioning.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FailedUserCreationsPanel;